/**
 * Stable PostHog identity helpers for Eagle VDR.
 * Prefer opaque `energy-user-<id>` (matches auth-server) over email-as-distinct_id.
 *
 * Person props are an allowlist only. Authenticated users get a validated display
 * name; email and free-text firm names never leave the application.
 */

const INTERNAL_DOMAINS = new Set(['data2dollars.ai', 'firebrandres.com']);

/** Keys allowed on PostHog person / $set payloads (client + tests). */
export const PERSON_PROP_ALLOWLIST = Object.freeze([
  'name',
  'role',
  'firm',
  'partner_tier',
  'app',
  'investor_firm_id',
]);

export function opaqueDistinctId(user) {
  const id = user?.id;
  if (id == null || id === '') return null;
  const n = Number(id);
  if (!Number.isFinite(n)) return null;
  return `energy-user-${Math.trunc(n)}`;
}

export function emailDomain(email) {
  if (!email || typeof email !== 'string') return null;
  const at = email.lastIndexOf('@');
  if (at < 0) return null;
  return email.slice(at + 1).trim().toLowerCase() || null;
}

/** Firm key for person props — domain only, never free-text company names. */
export function firmKeyFromEmail(email) {
  return emailDomain(email);
}

/** Real-name display label for PostHog people; never accept an email as a name. */
export function displayNameFromUser(user) {
  if (typeof user?.name !== 'string') return undefined;
  const name = user.name.trim();
  if (!name || name.length > 120 || looksLikeEmail(name)) return undefined;
  return name;
}

/**
 * partner_tier:
 * - internal: D2D / Firebrand desk
 * - partner: external desk (e.g. Cree Oil)
 * - investor: portal role
 */
export function partnerTier(user) {
  if (!user) return null;
  if (user.role === 'investor') return 'investor';
  const domain = emailDomain(user.email);
  if (domain && INTERNAL_DOMAINS.has(domain)) return 'internal';
  if (
    user.role === 'admin' ||
    user.role === 'user' ||
    user.role === 'platform_super_admin'
  ) {
    return 'partner';
  }
  return 'partner';
}

/**
 * Allowlisted person properties only. Email is used locally to derive `firm`
 * (domain) and tier, then discarded. `name` is an authenticated display label.
 */
export function personPropsFromUser(user) {
  if (!user) return {};
  const domain = firmKeyFromEmail(user.email);
  const tier = partnerTier(user);
  const props = {
    role: user.role || undefined,
    firm: domain || undefined,
    partner_tier: tier || undefined,
    app: 'eagle-vdr',
  };
  const displayName = displayNameFromUser(user);
  if (displayName) props.name = displayName;
  if (user.investor_firm_id != null) {
    props.investor_firm_id = user.investor_firm_id;
  }
  return props;
}

/** True if value looks like an email or contains an @ (reject as distinct_id / prop). */
export function looksLikeEmail(value) {
  return typeof value === 'string' && value.includes('@');
}

/** Assert analytics person props follow the approved privacy allowlist. */
export function assertSafePersonProps(props) {
  if (!props || typeof props !== 'object') return true;
  for (const [key, value] of Object.entries(props)) {
    if (key === 'email') {
      throw new Error('analytics props must not include email');
    }
    if (key === 'name' && (typeof value !== 'string' || looksLikeEmail(value))) {
      throw new Error('analytics display name must be a non-email string');
    }
    if (key !== 'name' && looksLikeEmail(value)) {
      throw new Error(`analytics props must not include email-shaped value for ${key}`);
    }
    if (!PERSON_PROP_ALLOWLIST.includes(key)) {
      throw new Error(`analytics prop ${key} is not in allowlist`);
    }
  }
  return true;
}
